import { useAuth0 } from "@auth0/auth0-react";
import Button from "Components/Atoms/Button/Button";
import Container from "Components/Atoms/Container/Container";
import Title from "Components/Atoms/Title/Title";
import ButtonGroup from "Components/Molecules/ButtonGroup/ButtonGroup";
import Modal from "Components/Molecules/Modals/Modal/Modal";
import ModalFooter from "Components/Molecules/Modals/ModalFooter/ModalFooter";
import ModalHeader from "Components/Molecules/Modals/ModalHeader/ModalHeader";
import { useEffect, useMemo, useState } from "react";
import DualListBox from 'react-dual-listbox';
import 'react-dual-listbox/lib/react-dual-listbox.css';
import { connect } from "react-redux";
import { useUserMetadata } from "Services/Authentication/Auth0/Auth0Service";
import { prepareDualListBox } from "Services/helpers";
import { StoreState } from "Store/Reducers";
import { patchEntity } from "Store/Settings/Entities/Actions";
import { getEntityById } from "Store/Settings/Entities/Selectors";
import { getFieldsById } from "Store/Settings/Fields/Selectors";
import { getUsedFieldsInLayoutsByEntityId } from "Store/Settings/Layout/Selectors";
import { updateUser } from "Store/User/Actions";
import { isUserPatching } from "Store/User/Selectors";

const ColumnModal = ({
    onClose,
    entity,
    fields, fieldsById,
    isPatching,
    ...props
}: ColumnModalProps) => {
    const { user, getAccessTokenSilently } = useAuth0();
    const user_metadata = useUserMetadata();
    const [selectedValue, setSelectedValue] = useState(user_metadata?.overview?.[entity.name] ?? []);
    const [isLoading, setLoading] = useState(false);

    const handleSave = () => {
        setLoading(true);
        props.updateUser(user?.sub, {
            user_metadata: {
                ...user_metadata,
                overview: {
                    ...user_metadata.overview,
                    [entity.name]: selectedValue
                }
            }
        });
    }

    const overviewFields = useMemo(() => {
        return [
            ...prepareDualListBox(fields?.map(i => fieldsById?.[i]) ?? []),
            ...[{
                value: 'owners',
                label: 'Intern verantwoordelijke'
            }, {
                value: 'updated_at',
                label: 'Laatst geupdate op',
            }, {
                value: 'created_at',
                label: 'Aangemaakt op'
            }]
        ]
    }, [fields]);

    const handleClose = async () => {
        await getAccessTokenSilently({ ignoreCache: true });
        onClose();
    }

    useEffect(() => {
        if (isLoading && !isPatching) handleClose();
    }, [isPatching]);

    return <Modal>
        <ModalHeader>
            <Title size='large' weight='bold'>
                Kolommen aanpassen
            </Title>
        </ModalHeader>
        <br/>
        <Container>
            {
                overviewFields &&
                <DualListBox
                    preserveSelectOrder
                    showOrderButtons
                    options={overviewFields}
                    selected={selectedValue}
                    onChange={setSelectedValue}
                    className='duallistbox'
                />
            }
        </Container>
        <br/>
        <ModalFooter>
            <ButtonGroup>
                <Button
                    variant="transparent"
                    size='large'
                    onClick={onClose}
                    isDisabled={isLoading}
                >
                    Annuleren
                </Button>
                <Button
                    color="primary"
                    variant="solid"
                    size='large'
                    isLoading={isLoading}
                    onClick={handleSave}
                >
                    Aanpassen
                </Button>
            </ButtonGroup>
        </ModalFooter>
    </Modal>
};

interface Props {
    entity_id: string
    onClose: any
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props.entity_id),
    fieldsById: getFieldsById(state),
    fields: getUsedFieldsInLayoutsByEntityId(state, props.entity_id),

    isPatching: isUserPatching(state)
});

const mapDispatch = {
    patchEntity,
    updateUser
}

type ColumnModalProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(ColumnModal);